import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { url } from '../components/utils/constants';
import { useApiError } from '../hooks/useApiError';
import { useNotificationManager } from '../hooks/useNotificationManager';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

// Função para renovar token
async function renewToken() {
  try {
    const token = localStorage.getItem('token');
    const { data } = await axios.get(url + '/api/v1/renewToken', {
      headers: {
        Authorization: 'Bearer ' + token
      }
    });

    if (data && data.token) {
      localStorage.setItem('token', data.token);
      return data.token;
    }
  } catch (error) {
    console.error('Erro ao renovar o token:', error);
    throw error;
  }
}

// Função para verificar expiração do token (otimizada)
async function checkTokenExpiry() {
  const token = localStorage.getItem('token');

  try {
    if (token) {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      const expiryTime = decodedToken.exp;
      const timeRemaining = expiryTime - currentTime;
      const tenMinutesInSec = 10 * 60;

      // Só renovar se estiver próximo do vencimento (10 minutos)
      if (timeRemaining < tenMinutesInSec && timeRemaining > 0) {
        console.log('🔄 Token próximo do vencimento, renovando...');
        await renewToken();
      } else if (timeRemaining < 0) {
        console.log('❌ Token expirado, removendo dados');
        localStorage.removeItem('token');
        localStorage.removeItem('fullname');
        localStorage.removeItem('role');
        localStorage.removeItem('email');
        return false; // Token expirado
      }
      return true; // Token válido
    }
    return false; // Sem token
  } catch (error) {
    console.error('💥 Erro ao verificar a expiração do token:', error);
    return false;
  }
}

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState({
    fullname: '',
    role: '',
    email: '',
    empresaLogo: ''
  });
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const { error, handleError } = useApiError();
  const { showError, showSuccess, clearHistory } = useNotificationManager();

  // Verificar autenticação inicial
  useEffect(() => {
    // Evitar múltiplas verificações
    if (authChecked) return;

    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        console.log('🔍 Token encontrado no localStorage:', token ? 'Sim' : 'Não');

        if (token) {
          const isValid = await checkTokenExpiry();
          console.log('⏰ Token válido (expiração):', isValid);

          if (isValid) {
            console.log('🔐 Verificando token no backend...');
            const response = await axios.get(url + '/api/v1/user/verifyToken', {
              headers: {
                Authorization: 'Bearer ' + token
              }
            });

            console.log('📡 Resposta do verifyToken:', response.status, response.data);

            if (response.data) {
              console.log('✅ Token verificado com sucesso');
              setIsLoggedIn(true);
              setUser({
                fullname: localStorage.getItem('fullname') || '',
                role: localStorage.getItem('role') || '',
                email: localStorage.getItem('email') || '',
                empresaLogo: localStorage.getItem('empresaLogo') || ''
              });
            } else {
              console.log('❌ Resposta vazia do verifyToken');
              logout();
            }
          } else {
            console.log('❌ Token expirado ou inválido');
            logout();
          }
        } else {
          console.log('❌ Nenhum token encontrado');
        }
      } catch (error) {
        console.error('💥 Erro na verificação inicial:', error);
        console.error('Status:', error.response?.status);
        console.error('Data:', error.response?.data);
        handleError(error);
        logout();
        
        // Só mostrar "Sessão expirada" se realmente havia um token
        const token = localStorage.getItem('token');
        if (token) {
          showError("Sessão expirada", {
            description: "Faça login novamente para continuar",
            duration: 4000,
          });
        }
      } finally {
        setLoading(false);
        setAuthChecked(true);
      }
    };

    checkAuth();
  }, []); // Removido authChecked da dependência para evitar loops

  // Verificar token periodicamente (menos agressivo)
  useEffect(() => {
    if (isLoggedIn) {
      console.log('⏰ Iniciando verificação periódica do token');
      const interval = setInterval(async () => {
        // Só verificar se o usuário ainda está ativo (não fazer logout desnecessário)
        const token = localStorage.getItem('token');
        if (!token) {
          console.log('❌ Token removido, fazendo logout');
          logout();
          return;
        }

        // Verificar apenas se o token está próximo do vencimento (5 minutos)
        try {
          const decodedToken = jwtDecode(token);
          const currentTime = Date.now() / 1000;
          const expiryTime = decodedToken.exp;
          const timeRemaining = expiryTime - currentTime;
          const fiveMinutesInSec = 5 * 60;

          if (timeRemaining < fiveMinutesInSec && timeRemaining > 0) {
            console.log('🔄 Token próximo do vencimento, renovando...');
            await renewToken();
          } else if (timeRemaining < 0) {
            console.log('❌ Token expirado, fazendo logout');
            logout();
          } else {
            console.log('✅ Token válido na verificação periódica');
          }
        } catch (error) {
          console.log('❌ Erro ao verificar token, fazendo logout');
          logout();
        }
      }, 300000); // Verificar a cada 5 minutos (menos frequente)

      return () => {
        console.log('🛑 Parando verificação periódica do token');
        clearInterval(interval);
      };
    }
  }, [isLoggedIn]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(url + '/api/v1/user/signin', {
        email,
        password
      });

      if (response.data) {
        const { data } = response;

        // Salvar dados no localStorage
        localStorage.setItem('fullname', data.user.fullname);
        localStorage.setItem('role', data.user.role);
        localStorage.setItem('email', data.user.email);
        localStorage.setItem('empresaLogo', data.user.empresaLogo || '');
        localStorage.setItem('token', data.token);

        // Atualizar estado
        setIsLoggedIn(true);
        setUser({
          fullname: data.user.fullname,
          role: data.user.role,
          email: data.user.email,
          empresaLogo: data.user.empresaLogo || ''
        });

        return { success: true, data: data.user };
      }
    } catch (error) {
      console.error('Erro no login:', error);
      handleError(error);

      const errorMessage = error.response?.data?.message || 'Erro ao fazer login';

      // Usar o gerenciador de notificações para evitar múltiplos popups
      showError("Erro no login", {
        description: errorMessage,
        duration: 4000,
      });

      return {
        success: false,
        error: errorMessage
      };
    }
  };

  const logout = () => {
    console.log('🚪 Fazendo logout do usuário');
    localStorage.removeItem('token');
    localStorage.removeItem('fullname');
    localStorage.removeItem('role');
    localStorage.removeItem('email');
    localStorage.removeItem('empresaLogo');
    setIsLoggedIn(false);
    setUser({
      fullname: '',
      role: '',
      email: '',
      empresaLogo: ''
    });
  };

  const updateUserData = (newData) => {
    setUser(prev => ({ ...prev, ...newData }));
    if (newData.fullname) localStorage.setItem('fullname', newData.fullname);
    if (newData.role) localStorage.setItem('role', newData.role);
    if (newData.email) localStorage.setItem('email', newData.email);
    if (newData.empresaLogo) localStorage.setItem('empresaLogo', newData.empresaLogo);
  };

  const getToken = () => {
    return localStorage.getItem('token');
  };

  const value = {
    isLoggedIn,
    user,
    loading,
    login,
    logout,
    updateUserData,
    getToken,
    error
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
